import React, { type RefObject, useCallback, useContext, useEffect, useRef, useState } from "react";
import { CalendarDay, type CalendarDayShape, DayPicker, type ModifiersShape } from "react-dates";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import { createPortal } from "react-dom";
import { graphql, QueryRenderer, useFragment } from "react-relay";

import { faAngleLeft, faAngleRight } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { groupBy } from "lodash";
import moment, { type Moment } from "moment";
import { fetchQuery } from "relay-runtime";

import AppContext from "~/common/AppContext";
import ViewportContext from "~/common/ViewportContext";
import WebsocketClient, { GroupEmitter } from "~/lib/websocket";
import { environment } from "~/models";

import type { DatePickerAgenda_viewer$key } from "~/__generated__/DatePickerAgenda_viewer.graphql";
import type { DatePickerQuery } from "~/__generated__/DatePickerQuery.graphql";

import "~/common/css/datepicker.css";

export type Day = { start: Moment; end: Moment };

const datePickerQuery = graphql`
	query DatePickerQuery($end: DateTime, $start: DateTime) {
		viewer {
			dayEvents: events(start: $start, end: $end) {
				edges {
					node {
						id
						start
						end
					}
				}
			}
		}
	}
`;

// The smallest weekday format moment offers by default is Su, Mo, Tu, etc
moment.updateLocale("en", { weekdaysMin: ["S", "M", "T", "W", "T", "F", "S", "S"] });

export interface DatePickerProps {
	/**
	 * An optional element ID to use as a portal for the displayed date picker.
	 *
	 * If nothing is provided the calendar will render in-place.
	 */
	selectorElem?: string | HTMLElement;
	/**
	 * A callback to call when the month currently being displayed in the widget
	 * changes.
	 */
	onVisibleMonthChange?: (direction: 1 | -1) => void;
	/**
	 * A callback to call when a date is selected in the calendar.
	 */
	onDateChange?: (date: Moment) => void;
	/**
	 * A callback to call when the date picker's visibility changes.
	 */
	onFocusChange?: (focused: boolean | null) => void;
	/**
	 * The date that should be selected when the calendar is opened.
	 */
	date?: Moment;
	/**
	 * The month that should be displayed when the calendar is opened.
	 */
	visibleMonth?: Moment;

	/**
	 * First date that a user should be able to select. Eg if this is January 2nd the user
	 * won't be able to select January 1st.
	 */
	firstSelectableDate?: Moment;

	// Optional props passed down into react-dates.
	daySize?: number;
	renderDayContents?: (date: Moment) => JSX.Element;
	renderMonthElement?: (args: { month: Moment }) => JSX.Element;
	calendarRef?: RefObject<HTMLDivElement>;
}

/**
 * Generic date-picker widget.
 */
export function DatePicker(props: DatePickerProps) {
	const { isIPhoneSE } = useContext(ViewportContext);
	const { user } = useContext(AppContext);
	const { onVisibleMonthChange, onFocusChange, firstSelectableDate: firstSelectableDateRaw } = props;
	const firstSelectableDate: Moment | undefined = React.useMemo(
		() => firstSelectableDateRaw?.clone().startOf("day"),
		[firstSelectableDateRaw],
	);
	const firstSelectableMonth: Moment | undefined = React.useMemo(
		() => firstSelectableDate?.clone().startOf("month"),
		[firstSelectableDate],
	);
	let [visibleMonth, setVisibleMonth] = useState(() => moment(props.date));
	const rawToday = moment().startOf("day").toString();
	// create a version of today that is referentially stable, eg won't update unless the day changes.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const today = React.useMemo(() => moment().startOf("day"), [rawToday]);

	const onMonthChange = useCallback(
		(direction: 1 | -1) => {
			setVisibleMonth(moment(visibleMonth).add(direction, "months"));
			if (onVisibleMonthChange) onVisibleMonthChange(direction);
		},
		[visibleMonth, onVisibleMonthChange],
	);

	const { onDateChange: propsOnDateChange } = props;
	const onDateChange = React.useCallback(
		(newDate: Moment) => {
			if (firstSelectableDate && newDate.isBefore(firstSelectableDate.startOf("day"))) return;
			if (propsOnDateChange) propsOnDateChange(moment(newDate).startOf("day"));
			if (onFocusChange) onFocusChange(false);
		},
		[firstSelectableDate, propsOnDateChange, onFocusChange],
	);

	const { renderDayContents: propsRenderDayContents } = props;
	const renderDayContents = React.useCallback(
		(newDate: Moment) => {
			if (propsRenderDayContents) {
				return propsRenderDayContents(newDate);
			}
			return <>{newDate.format("D")}</>;
		},
		[propsRenderDayContents],
	);

	const renderCalendarDay = React.useCallback(
		(calendarDayProps: CalendarDayShape) => {
			const day = calendarDayProps.day;

			const isSelected = day?.isSame(moment(props.date), "day") || false;
			const isSelectable = firstSelectableDate ? !day?.isBefore(firstSelectableDate) : true;

			const originalModifiers: ModifiersShape = calendarDayProps.modifiers
				? new Set(calendarDayProps.modifiers)
				: new Set();
			if (!isSelectable) {
				originalModifiers.add("blocked-out-of-range");
			}
			if (isSelected) {
				originalModifiers.add("selected");
			}
			if (day && day.isSame(today, "day")) {
				originalModifiers.add("today");
			}

			return <CalendarDay {...calendarDayProps} modifiers={originalModifiers} />;
		},
		[today, props.date, firstSelectableDate],
	);

	const { renderMonthElement: propsRenderMonthElement } = props;
	const renderMonthElement = React.useCallback(
		({ month }: { month: Moment }) => {
			if (propsRenderMonthElement) {
				return propsRenderMonthElement({ month: month });
			}

			return (
				<>
					<span className="month">{month.format("MMMM")}</span>
					<span className="year">{month.format("YYYY")}</span>
				</>
			);
		},
		[propsRenderMonthElement],
	);

	const initializeCalendarMonth = useCallback(() => {
		const initialVisibleDate = props.date === undefined ? moment() : props.date;

		let initialVisibleMonth = initialVisibleDate.clone();

		if (firstSelectableMonth) {
			if (initialVisibleMonth.isBefore(firstSelectableMonth)) {
				initialVisibleMonth = firstSelectableMonth.clone();
			}
		}

		// We set visibleMonth here as we don't consistently get visibility state changes
		// from the <DayPicker> component. This method is only called when the calendar is shown
		setVisibleMonth(initialVisibleMonth);
		return initialVisibleMonth;
	}, [firstSelectableMonth, props.date]);

	const prevMonth = useCallback(() => {
		onMonthChange(-1);
	}, [onMonthChange]);

	const nextMonth = useCallback(() => {
		onMonthChange(1);
	}, [onMonthChange]);

	const daySize = isIPhoneSE ? Math.floor(window.innerWidth / 8) : 32;
	let firstWeekDay;
	if (user?.internalAttributes?.firstWeekDay === "Mon") {
		firstWeekDay = 1 as const;
	} else if (user?.internalAttributes?.firstWeekDay === "Sat") {
		firstWeekDay = 6 as const;
	} else {
		firstWeekDay = 0 as const;
	}

	const component = (
		<div className={"date-picker"} ref={props.calendarRef}>
			<DayPicker
				daySize={props.daySize ? props.daySize : daySize}
				enableOutsideDays
				numberOfMonths={1}
				firstDayOfWeek={firstWeekDay}
				initialVisibleMonth={initializeCalendarMonth}
				disablePrev={firstSelectableMonth && !(visibleMonth.startOf("month") > firstSelectableMonth)}
				onPrevMonthClick={prevMonth}
				onNextMonthClick={nextMonth}
				renderCalendarDay={renderCalendarDay}
				renderDayContents={renderDayContents}
				renderMonthElement={renderMonthElement}
				horizontalMonthPadding={0}
				navPrev={
					visibleMonth && (
						<div>
							<FontAwesomeIcon fixedWidth icon={faAngleLeft} />
							{moment(visibleMonth).add(-1, "months").format("MMM")}
						</div>
					)
				}
				navNext={
					visibleMonth && (
						<div>
							{moment(visibleMonth).add(1, "months").format("MMM")}
							<FontAwesomeIcon fixedWidth icon={faAngleRight} />
						</div>
					)
				}
				onDayClick={onDateChange}
			/>
		</div>
	);

	if (props.selectorElem) {
		let mountPoint;

		if (props.selectorElem instanceof HTMLElement) {
			mountPoint = props.selectorElem;
		} else {
			mountPoint = document.getElementById(props.selectorElem);
		}

		if (mountPoint) {
			return createPortal(component, mountPoint);
		}
	}

	return component;
}

const agendaDatePickerFragment = graphql`
	fragment DatePickerAgenda_viewer on Viewer {
		calendars {
			edges {
				node {
					id
				}
			}
		}
	}
`;

interface AgendaDatePickerProps extends DatePickerProps {
	viewer: DatePickerAgenda_viewer$key;
}

/**
 * A date picker for the agenda page.
 *
 * Listens to websocket notifications and updates itself when the user's
 * calendar(s) change.
 */
export function AgendaDatePicker(props: AgendaDatePickerProps) {
	const { date: startingDate = moment() } = props;
	let [visibleMonth, setVisibleMonth] = useState(moment(startingDate));
	const groups = useRef<GroupEmitter[]>([]);
	const { calendars } = useFragment(agendaDatePickerFragment, props.viewer);

	// Render an individual date cell in the calendar.
	const renderDayContents = useCallback((events: { [s: string]: Day[] }, date: Moment) => {
		const dateKey = date.format("YYYY-MM-DD");
		const eventsToday = events[dateKey];

		return (
			<div>
				<span>{date.format("D")}</span>
				{eventsToday && (
					<div className="dot-container">
						<div className="dot" />
					</div>
				)}
			</div>
		);
	}, []);

	// If a calendar is updated, refresh the calendar view.
	const onVisibleMonthChange = useCallback(
		(direction: 1 | -1) => {
			setVisibleMonth(moment(visibleMonth).add(direction, "months"));
		},
		[visibleMonth],
	);

	const onQueryResult = useCallback(
		({ props: response }: { props: DatePickerQuery["response"] | null }) => {
			let eventDates: { [s: string]: Day[] } = {};

			if (response) {
				// Create calendar markers for events in the currently visible date
				// range.
				eventDates = groupBy(
					response.viewer.dayEvents.edges.reduce((result, edge) => {
						if (edge && edge.node.start && edge.node.end) {
							result.push({ start: moment(edge.node.start), end: moment(edge.node.end) });
						}
						return result;
					}, [] as Day[]),
					event => moment(event.start).format("YYYY-MM-DD"),
				);
			}

			function renderDay(date: Moment) {
				return renderDayContents(eventDates, date);
			}

			// eslint-disable-next-line react/jsx-no-bind
			return <DatePicker {...props} renderDayContents={renderDay} onVisibleMonthChange={onVisibleMonthChange} />;
		},
		[onVisibleMonthChange, renderDayContents, props],
	);

	const calendarGroupMessage = useCallback(
		(data: { type: string }) => {
			if (data.type == "CALENDAR_UPDATED") {
				window.relayCache?.clear();
				fetchQuery(environment, datePickerQuery, {
					start: moment(visibleMonth).startOf("month").subtract(7, "day").toISOString(),
					end: moment(visibleMonth).endOf("month").add(7, "day").toISOString(),
				}).toPromise();
			}
		},
		[visibleMonth],
	);

	// On mount, subscribe to calendar change notifications.
	useEffect(() => {
		if (!calendars) return;

		for (let edge of calendars.edges) {
			if (!edge || !edge.node) {
				continue;
			}
			const group = WebsocketClient.getInstance().getOrJoinGroup(`calendars.${edge.node.id.split("=").join("")}`, {});
			group.on("message", calendarGroupMessage);
			groups.current.push(group);
		}

		return () => {
			for (let socket of groups.current) {
				socket.off("message", calendarGroupMessage);
				socket.close();
			}
			groups.current = [];
		};
	}, [calendars, calendarGroupMessage]);

	return (
		<QueryRenderer<DatePickerQuery>
			query={datePickerQuery}
			environment={environment}
			variables={{
				start: moment(visibleMonth).startOf("month").subtract(7, "day").toISOString(),
				end: moment(visibleMonth).endOf("month").add(7, "day").toISOString(),
			}}
			render={onQueryResult}
		/>
	);
}
